import React from "react";
import difference from "ramda/src/difference";

import { useAppSelector } from "./redux";

export enum USER_CLAIMS_LIST {
  invoiceService = "invoiceService",
  shopService = "shopService",
  userClaimTest = "userClaimTest",
}

export type ClaimsListAllowedKeys = keyof typeof USER_CLAIMS_LIST;

/**
 * Checks if the user has the required permissions.
 *
 * @param {ClaimsListAllowedKeys[]} claims - An array of required permission claims.
 * @returns {{ hasPermission: boolean }} An object with a boolean property indicating whether the user has all the required permissions.
 */
export const useUserClaim = (claims: ClaimsListAllowedKeys[]) => {
  /**
   * @type {string[]|null} user claims - The user's permission claims.
   */
  const auth = useAppSelector((state) => state.auth);

  /**
   * @type {boolean} hasPermission - Indicates whether the user has all the required permissions.
   */
  const hasPermission = React.useMemo(() => {
    if (!auth.userClaims) return false;
    const differenceClaims = difference(claims, auth.userClaims);
    return differenceClaims.length === 0;
  }, [claims, auth]);

  return { hasPermission };
};
